import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { makeStyles, mergeClasses, tokens, Spinner } from '@coreai/fluentui-react';
import { Apps20Filled, Folder20Filled, Folder20Regular } from '@fluentui/react-icons';
import { useCurrentApp } from '@/hooks/useCurrentApp';
import { ResourceErrorPage } from '@/components/shared/ResourceErrorPage';
import { PermissionBanner } from '@/components/shared/PermissionBanner';
import { ApiError } from '@/services/apiError';
import { getAppNavSections, hasNavPermission } from '@/components/Sidebar/appSidebarConfig';
import { Sidebar, type SidebarSection } from '@/components/Sidebar/Sidebar';
import { useTelemetry } from '@/hooks/useTelemetry';
import { useAppStore } from '@/stores/appStore';
import { useRecentWorkStore } from '@/stores/recentWorkStore';
import { useAppPermission } from '@/hooks/useAppPermission';
import { paths, tenantPrefix } from '@/routes';
import { useIsProjectOwner } from '@/hooks/useUserPermissions';
import { CreateWorkflowDialogHost } from '@/components/CreateWorkflowDialogHost';
import { usePrefetchOperations } from '@/hooks/useAllOperations';
import { useAssistantFeatures } from '@/hooks/useWorkflowAssistantFeatures';
import { useAppShellStyles } from './AppShell.styles';
import { APP_SHELL_MOBILE_QUERY } from '@/theme/appShell';

const PERMISSION_DENIED_REASON = "You don't have the correct permissions to view this";

const useStyles = makeStyles({
  mainContent: {
    overflow: 'auto',
  },
  content: {
    // Asymmetric top padding pulls the App overview title (and any other
    // page-level heading) up so it sits alongside the sidebar Home button
    // instead of leaving a large gap under the global header.
    paddingTop: tokens.spacingVerticalM,
    paddingRight: tokens.spacingHorizontalXXXL,
    paddingBottom: tokens.spacingVerticalXXXL,
    paddingLeft: tokens.spacingHorizontalXXXL,
    flex: 1,
    // Becomes a flex column so inner pages can opt into their own scroll
    // region (e.g. WorkflowListPage's table) instead of forcing the entire
    // main area to scroll. minHeight: 0 lets flex children shrink below
    // their natural content size.
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    [`@media ${APP_SHELL_MOBILE_QUERY}`]: {
      paddingTop: tokens.spacingVerticalS,
      paddingRight: tokens.spacingHorizontalM,
      paddingBottom: tokens.spacingVerticalL,
      paddingLeft: tokens.spacingHorizontalM,
    },
  },
  contentFullWidth: {
    padding: 0,
    maxWidth: 'none',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    overflow: 'hidden',
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
    padding: tokens.spacingVerticalXXXL,
  },
});

export const AppLayout = () => {
  const styles = useStyles();
  const shellStyles = useAppShellStyles();
  const { trackEvent } = useTelemetry();
  const location = useLocation();
  const { app, projectName, locationName, isLoading, notFound, error } = useCurrentApp();
  const locationInStore = useAppStore((state) => state.selectedLocation);
  const selectedTenantId = useAppStore((state) => state.selectedTenantId);
  const { permission, canRead, canEdit, isLoading: permissionLoading } = useAppPermission();
  const { data: isProjectAdmin } = useIsProjectOwner(projectName ?? '');
  const prevContextRef = useRef('');

  // Begin prefetching the operation-picker corpus the moment we have an app
  // scope (project + region). Internally idempotent and gated on `hasScope`,
  // so calling it here — above the designer route — covers both direct-URL
  // entry (`/.../workflows/myWorkflow`) and dashboard entry (workflow list).
  // By the time the user clicks "Add action" the corpus is usually warm.
  usePrefetchOperations();

  // Load assistant capability flags into the app store.
  useAssistantFeatures();

  // Bridge URL-derived app context into the store for child pages. Must run
  // in an effect — writing to Zustand during render notifies other subscribers
  // (FluentProvider, etc.) and triggers a React render-phase setState warning.
  useEffect(() => {
    if (!app || !projectName) return;
    const contextKey = `${locationName ?? ''}|${projectName}|${app.name}`;
    if (contextKey === prevContextRef.current) return;
    prevContextRef.current = contextKey;
    useAppStore.getState().hydrateRouteContext({ projectName, app, ...(locationName ? { locationName } : {}) });
    const tenantId = useAppStore.getState().selectedTenantId;
    const recordLocation = locationName ?? locationInStore;
    if (tenantId && recordLocation) {
      useRecentWorkStore.getState().recordAccess({
        type: 'app',
        displayName: app.name,
        location: recordLocation,
        project: projectName,
        app: app.name,
        href: paths.appWorkflows(recordLocation, projectName, app.name, tenantId),
        tenantId,
      });
    }
  }, [app, projectName, locationName, locationInStore]);

  // Hide sidebar on workflow designer routes (full-width canvas)
  const isDesignerRoute = location.pathname.includes('/workflows/');

  const hasNoAccess = useMemo(() => !permissionLoading && !canRead && !canEdit && !isProjectAdmin, [permissionLoading, canRead, canEdit, isProjectAdmin]);

  // Map config + permission state to the shared Sidebar's item shape.
  // Three pieces of runtime context get woven in here:
  //   - Home item path → tenant-aware so it stays inside the active tenant.
  //   - The unlabelled Home section gets a "Project" link appended so the
  //     user can jump back to the parent project. It uses the project's
  //     name as its label (ellipsizes when long).
  //   - The Application section heading is overridden with the live app
  //     name and an app icon, and `indentItems` nests its children visually.
  const sidebarSections: SidebarSection[] = useMemo(() => {
    const homeHref = tenantPrefix(selectedTenantId) || '/';
    const projectHref =
      projectName && (locationName ?? locationInStore)
        ? paths.projectApps(
            (locationName ?? locationInStore) as string,
            projectName,
            selectedTenantId,
          )
        : undefined;
    const appDisplayName = app?.name || 'Application';

    const sections: SidebarSection[] = [];

    for (const section of getAppNavSections()) {
      const baseItems = section.items.map((item) => {
        const allowed = hasNavPermission(item, permission);
        const path = item.id === 'home' ? homeHref : item.path;
        return {
          id: item.id,
          label: item.label,
          icon: item.icon,
          activeIcon: item.activeIcon,
          path,
          end: item.end,
          disabled: !allowed && !permissionLoading,
          disabledReason: !allowed && !permissionLoading ? PERMISSION_DENIED_REASON : undefined,
        };
      });

      if (section.id === 'home') {
        // Home item sits alone at the top without a label or divider.
        sections.push({ id: section.id, label: section.label, items: baseItems });

        // Project link gets its own section with a categoryLabel for the
        // small uppercase "PROJECT" title (matching the "APP" styling).
        // The project name is a clickable nav item that navigates back
        // to the parent project.
        if (projectHref && projectName) {
          sections.push({
            id: 'project',
            categoryLabel: 'Environment',
            items: [
              {
                id: 'project-link',
                label: projectName,
                tooltipContent: `Environment: ${projectName}`,
                icon: Folder20Regular,
                activeIcon: Folder20Filled,
                path: projectHref,
                end: true,
              },
            ],
          });
        }
      } else if (section.id === 'application') {
        sections.push({
          id: section.id,
          categoryLabel: 'App',
          label: appDisplayName,
          icon: Apps20Filled,
          brandIcon: true,
          indentItems: true,
          items: baseItems,
        });
      } else {
        sections.push({ id: section.id, label: section.label, items: baseItems });
      }
    }

    return sections;
  }, [permission, permissionLoading, selectedTenantId, projectName, locationName, locationInStore, app]);

  const handleSidebarItemClick = useCallback(
    (id: string) => trackEvent('navigation.sidebar.clicked', { destination: id }),
    [trackEvent],
  );

  if (isLoading) {
    return (
      <div className={shellStyles.body}>
        <div className={mergeClasses(shellStyles.main, styles.loadingContainer)}>
          <Spinner size="medium" label="Loading application…" />
        </div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className={shellStyles.body}>
        <div className={shellStyles.main}>
          <ResourceErrorPage resourceType="application" error={new ApiError('Not found', 404)} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={shellStyles.body}>
        <div className={shellStyles.main}>
          <ResourceErrorPage resourceType="application" error={error} />
        </div>
      </div>
    );
  }

  // Full-width layout for designer — no sidebar
  if (isDesignerRoute) {
    if (permissionLoading) {
      return <div className={mergeClasses(shellStyles.main, styles.loadingContainer)}><Spinner size="medium" label="Checking permissions…" /></div>;
    }
    if (hasNoAccess) {
      return <div className={mergeClasses(shellStyles.main, styles.content)}><PermissionBanner title="Access denied" message="You don't have the correct permissions to access this application. Contact the application administrator to request access." /></div>;
    }
    return (
      <div className={mergeClasses(shellStyles.main, styles.contentFullWidth)}>
        <Outlet />
        <CreateWorkflowDialogHost />
      </div>
    );
  }

  return (
    <div className={shellStyles.body}>
      <Sidebar
        ariaLabel="Application navigation"
        navAriaLabel="Application"
        sections={sidebarSections}
        onItemClick={handleSidebarItemClick}
      />
      <div className={mergeClasses(shellStyles.main, styles.mainContent)}>
        <div className={styles.content}>
          {permissionLoading ? (
            <div className={styles.loadingContainer}>
              <Spinner size="small" label="Checking permissions…" />
            </div>
          ) : hasNoAccess ? (
            <PermissionBanner
              title="Access denied"
              message="You don't have the correct permissions to access this application. Contact the application administrator to request access."
            />
          ) : <Outlet />}
        </div>
      </div>

      <CreateWorkflowDialogHost />
    </div>
  );
};
